import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import LoadingSpinner from '../LoadingSpinner';
import ErrorCard from '../ErrorCard';
import StripeForm from './gateways/StripeForm';
import NmiCollectForm from './gateways/NmiCollectForm';
import AuthorizeNetAcceptForm from './gateways/AuthorizeNetAcceptForm';
import api from '../../services/api';
// Payment brand icons (React components)
import { Visa, Mastercard, Amex, Discover, Jcb } from 'react-payment-logos/dist/flat';

// ===== STYLED COMPONENTS =====
const PaymentSection = styled.section`
  margin-bottom: var(--zyvgo-spacing-xl, 32px);
`;

// Order Summary Section
const OrderSummary = styled.div`
  background-color: var(--zyvgo-card-bg, #FFFFFF);
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: var(--zyvgo-spacing-lg, 24px);
  margin-bottom: var(--zyvgo-spacing-lg, 24px);
`;

const OrderSummaryTitle = styled.h3`
  font-size: var(--zyvgo-heading-size, 1.5rem);
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
  margin: 0 0 var(--zyvgo-spacing-md, 16px) 0;
`;

const PriceGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--zyvgo-spacing-xs, 8px);
  margin-bottom: var(--zyvgo-spacing-md, 16px);
`;

// Product row (image + metadata)
const ProductRow = styled.div`
  display: flex;
  gap: var(--zyvgo-spacing-md, 16px);
  align-items: center;
  margin-bottom: var(--zyvgo-spacing-md, 12px);
`;

const PillImageWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f7f9fb, #eef3f7);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 56px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
  display: block;
`;

const ProductText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductTitle = styled.span`
  font-size: var(--zyvgo-body-size, 1rem);
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
`;

const ProductSubtitle = styled.span`
  font-size: var(--zyvgo-small-size, 0.875rem);
  color: var(--zyvgo-text-secondary, #666666);
`;

const BillingLabel = styled.span`
  font-size: 0.75rem;
  color: var(--zyvgo-text-secondary, #888888);
  margin-top: 6px;
`;

const PriceLabel = styled.span`
  font-size: var(--zyvgo-body-size, 1rem);
  font-weight: 500;
  color: var(--zyvgo-text-primary, #222222);
`;

const OriginalPrice = styled.span`
  font-size: var(--zyvgo-body-size, 1rem);
  color: var(--zyvgo-text-secondary, #666666);
  text-decoration: line-through;
`;

const DiscountedPrice = styled.span`
  font-size: var(--zyvgo-heading-size, 1.5rem);
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
`;

const AmountDueGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--zyvgo-spacing-md, 16px);
  border-top: 1px solid var(--zyvgo-border, #e1e5e9);
`;

const AmountDueLabel = styled.span`
  font-size: var(--zyvgo-body-size, 1rem);
  font-weight: 500;
  color: var(--zyvgo-text-primary, #222222);
`;

const AmountDuePrice = styled.span`
  font-size: var(--zyvgo-heading-size, 1.5rem);
  font-weight: 600;
  color: #28a745;
`;

const OrderSummaryHelper = styled.p`
  font-size: var(--zyvgo-small-size, 0.875rem);
  color: var(--zyvgo-text-secondary, #666666);
  margin: var(--zyvgo-spacing-sm, 12px) 0 0 0;
  line-height: 1.5;
`;

// Payment Method Section
const PaymentMethodSection = styled.div`
  margin-bottom: var(--zyvgo-spacing-lg, 24px);
`;

const PaymentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--zyvgo-spacing-md, 16px);
`;

// Make the main title slightly smaller per design (secondary)
const PaymentTitle = styled.h3`
  font-size: var(--zyvgo-body-size, 1rem);
  font-weight: 500;
  color: var(--zyvgo-text-primary, #222222);
  margin: 0;
`;

const SecureBadge = styled.div`
  display: flex;
  align-items: center;
  gap: var(--zyvgo-spacing-xs, 8px);
  font-size: var(--zyvgo-small-size, 0.875rem);
  color: var(--zyvgo-text-secondary, #666666);
`;

const SecureIcon = styled.svg`
  width: 16px;
  height: 16px;
  color: var(--zyvgo-text-secondary, #666666);
`;

// Prominent subheading (primary visual weight)
const PaymentSubheading = styled.h4`
  font-size: var(--zyvgo-heading-size, 1.5rem);
  font-weight: 600;
  color: var(--zyvgo-text-primary, #222222);
  margin: 0 0 var(--zyvgo-spacing-sm, 12px) 0;
`;

const PaymentDescription = styled.p`
  font-size: var(--zyvgo-small-size, 0.875rem);
  color: var(--zyvgo-text-secondary, #666666);
  margin: 0 0 var(--zyvgo-spacing-lg, 24px) 0;
  line-height: 1.5;
`;

const SelectedMethodDisplay = styled.div`
  background-color: var(--zyvgo-bg-dark, #1a1a1a);
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: var(--zyvgo-spacing-md, 16px);
  margin-bottom: var(--zyvgo-spacing-lg, 24px);
`;

const MethodLabel = styled.span`
  font-size: var(--zyvgo-body-size, 1rem);
  font-weight: 500;
  color: white;
`;

const CardIcons = styled.div`
  display: flex;
  align-items: center;
  gap: var(--zyvgo-spacing-sm, 12px);
`;

const IconWrap = styled.div`
  width: 36px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// Use the official brand React components from react-payment-logos

// Payment Form Section
const PaymentFormSection = styled.div`
  margin-bottom: var(--zyvgo-spacing-lg, 24px);
`;

const PaymentForm = styled.form`
  background-color: var(--zyvgo-card-bg, #FFFFFF);
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-md, 8px);
  padding: var(--zyvgo-spacing-lg, 24px);
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: var(--zyvgo-spacing-md, 16px);
  background-color: var(--zyvgo-bg-dark, #1a1a1a);
  color: white;
  border: none;
  border-radius: var(--zyvgo-radius-sm, 4px);
  font-size: var(--zyvgo-body-size, 1rem);
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--zyvgo-bg-dark-hover, #333333);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(26, 26, 26, 0.5);
  }
`;

const SaveCardButton = styled(SubmitButton)`
  margin-top: var(--zyvgo-spacing-md, 16px);
`;

const PaymentFormHelper = styled.p`
  font-size: var(--zyvgo-small-size, 0.875rem);
  color: var(--zyvgo-text-secondary, #666666);
  /* small gap above helper to separate it from card fields */
  margin: 12px 0 0 0;
  line-height: 1.5;
  text-align: center;
`;

const SavedCardChoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
`;

const SavedCardOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 46px;
  padding: 11px 13px;
  border: 1px solid var(--zyvgo-border, #d0d5dd);
  border-radius: 8px;
  background: var(--zyvgo-card-bg, #fff);
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
  color: var(--zyvgo-text-primary, #101828);
  transition: border-color 0.16s, background 0.16s, box-shadow 0.16s;

  &:hover {
    border-color: #98a2b3;
    background: var(--zyvgo-bg-subtle, #f8fafc);
  }

  &.selected {
    border-color: var(--zyvgo-focus-border, #0f766e);
    background: #f0fdfa;
    box-shadow: 0 0 0 2px rgba(15, 118, 110, 0.1);
    color: var(--zyvgo-text-primary, #101828);
  }
`;

const SavedCardContent = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const SavedCardActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  flex: 0 0 auto;
`;

const SavedCardActionButton = styled.button`
  border: 1px solid currentColor;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1;
  padding: 6px 8px;
  opacity: 0.82;

  &:hover:not(:disabled) {
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const NewCardOption = styled(SavedCardOption)`
  &.selected {
    border-color: var(--zyvgo-focus-border, #0f766e);
    background: #f0fdfa;
    color: var(--zyvgo-text-primary, #101828);
  }
`;

// Coupon Section Styled Components
const CouponSection = styled.div`
  margin-top: var(--zyvgo-spacing-md, 16px);
  padding-top: var(--zyvgo-spacing-md, 16px);
  border-top: 1px solid var(--zyvgo-border-light, #e0e0e0);
`;

const CouponToggle = styled.button`
  background: none;
  border: none;
  color: var(--zyvgo-primary, #007bff);
  font-size: var(--zyvgo-small-size, 0.875rem);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CouponInputRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: var(--zyvgo-spacing-sm, 12px);
`;

const CouponInput = styled.input`
  flex: 1;
  min-width: 0; /* Fix for Firefox flex overflow */
  padding: 10px 12px;
  border: 1px solid var(--zyvgo-border, #e1e5e9);
  border-radius: var(--zyvgo-radius-sm, 4px);
  font-size: var(--zyvgo-body-size, 1rem);
  
  &:focus {
    outline: none;
    border-color: var(--zyvgo-primary, #007bff);
  }
  
  &.valid {
    border-color: #28a745;
  }
  
  &.invalid {
    border-color: #dc3545;
  }
`;

const CouponApplyButton = styled.button`
  padding: 10px 16px;
  background-color: var(--zyvgo-bg-dark, #1a1a1a);
  color: white;
  border: none;
  border-radius: var(--zyvgo-radius-sm, 4px);
  font-size: var(--zyvgo-body-size, 1rem);
  cursor: pointer;
  min-width: 80px;
  flex-shrink: 0;
  
  &:hover {
    background-color: var(--zyvgo-bg-dark-hover, #333333);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CouponSuccess = styled.div`
  margin-top: 8px;
  color: #28a745;
  font-size: var(--zyvgo-small-size, 0.875rem);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CouponError = styled.div`
  margin-top: 8px;
  color: #dc3545;
  font-size: var(--zyvgo-small-size, 0.875rem);
`;

const DiscountRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #28a745;
  font-size: var(--zyvgo-small-size, 0.875rem);
`;

const ErrorText = styled.span`
  display: block;
`;

const PaymentStep = forwardRef(
  (
    {
      onError,
      responses = {},
      questionnaire = null,
      mode = null,
      checkoutOrderSummary = null,
      checkoutPaymentConfig = null,
      checkoutSavedPaymentMethods = [],
      checkoutToken = null,
      checkoutCapability = null,
      checkoutSessionRevision = null,
      checkoutCouponDiscount = 0,
      checkoutAppliedCoupon = null,
      hideCheckoutOrderSummary = false,
      hidePaymentHeading = false,
      paymentAdapter = null,
    },
    ref
  ) => {
  const isCheckoutMode = mode === 'checkout';
  const [loading, setLoading] = useState(!isCheckoutMode && !paymentAdapter);
  const [error, setError] = useState(null);
  const [activeGateway, setActiveGateway] = useState(paymentAdapter?.gateway || null);
  const [gatewayConfig, setGatewayConfig] = useState(null);
  const childRef = useRef(null);

  // Checkout: saved card vs new card
  const [localSavedMethods, setLocalSavedMethods] = useState([]);
  useEffect(() => {
    setLocalSavedMethods(Array.isArray(checkoutSavedPaymentMethods) ? checkoutSavedPaymentMethods : []);
  }, [checkoutSavedPaymentMethods]);

  // Saved methods are processor-specific. Do not offer a card created for the
  // previous gateway after a tenant switches processors; submitting it would
  // either send the wrong identifier or make the checkout appear stuck on the
  // old payment form.
  const savedMethods = useMemo(() => localSavedMethods.filter((method) => {
    const gateway = String(activeGateway || '').toLowerCase().replace('-', '_');
    if (!gateway) return false;

    const processor = String(method?.processor || method?.gateway || '')
      .toLowerCase()
      .replace('-', '_');
    if (processor) {
      return (processor === 'authorizenet' ? 'authorize_net' : processor) === gateway;
    }

    if (gateway === 'stripe') return Boolean(method?.stripe_payment_method_id);
    if (gateway === 'nmi') return Boolean(method?.nmi_customer_vault_id);
    if (gateway === 'authorize_net') {
      return Boolean(method?.authnet_customer_profile_id && method?.authnet_payment_profile_id);
    }
    return false;
  }), [localSavedMethods, activeGateway]);
  const hasSavedMethods = isCheckoutMode && savedMethods.length > 0;
  const defaultSaved = savedMethods.find((m) => m.is_default) || savedMethods[0] || null;
  const [paymentChoice, setPaymentChoice] = useState('new');
  const [selectedSavedMethod, setSelectedSavedMethod] = useState(null);
  const [editingSavedMethod, setEditingSavedMethod] = useState(null);
  const [removingPaymentMethodId, setRemovingPaymentMethodId] = useState(null);
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);

  useEffect(() => {
    if (!isCheckoutMode) return;
    if (savedMethods.length > 0) {
      setSelectedSavedMethod(savedMethods.find((m) => m.is_default) || savedMethods[0]);
      setPaymentChoice('saved');
    } else {
      setPaymentChoice('new');
      setSelectedSavedMethod(null);
    }
  }, [isCheckoutMode, savedMethods]);

  const handleEditSavedMethod = (event, method) => {
    event.stopPropagation();
    setPaymentChoice('new');
    setEditingSavedMethod(method);
    setSelectedSavedMethod(null);
    setError(null);
  };

  const handleEnterNewCard = () => {
    setPaymentChoice('new');
    setSelectedSavedMethod(null);
    setEditingSavedMethod(null);
    setError(null);
  };

  const handleCardOptionKeyDown = (event, handler) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handler();
    }
  };

  const handleRemoveSavedMethod = async (event, method) => {
    event.stopPropagation();
    if (!checkoutToken && !(checkoutCapability && checkoutSessionRevision != null)) {
      setError('Checkout token is missing. Refresh the checkout link and try again.');
      return;
    }

    setError(null);
    setRemovingPaymentMethodId(method.id);
    try {
      if (checkoutCapability && checkoutSessionRevision != null) {
        await api.removeCombinedCheckoutPaymentMethod({
          checkoutCapability,
          runtimeSessionRevision: checkoutSessionRevision,
          paymentMethodId: method.id,
        });
      } else {
        await api.deleteCheckoutPaymentMethod(checkoutToken, method.id);
      }
      setLocalSavedMethods((current) => {
        const next = current.filter((item) => item.id !== method.id);
        if (!next.some((item) => item.is_default) && next.length > 0) {
          return next.map((item, index) => ({ ...item, is_default: index === 0 }));
        }
        return next;
      });
      if (selectedSavedMethod?.id === method.id) {
        setSelectedSavedMethod(null);
        setPaymentChoice('new');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove payment method.');
    } finally {
      setRemovingPaymentMethodId(null);
    }
  };

  const handleSaveEditedCard = async () => {
    if (!checkoutToken && !(checkoutCapability && checkoutSessionRevision != null)) {
      setError('Checkout token is missing. Refresh the checkout link and try again.');
      return;
    }
    if (!editingSavedMethod) {
      return;
    }
    if (!childRef.current || typeof childRef.current.getPaymentData !== 'function') {
      setError('Payment form is not ready. Please try again.');
      return;
    }

    setError(null);
    setSavingPaymentMethod(true);
    try {
      const paymentData = await childRef.current.getPaymentData();
      const savePayload = {
        gateway: activeGateway,
        ...paymentData,
        replace_payment_method_id: editingSavedMethod.id,
      };
      const response = checkoutCapability && checkoutSessionRevision != null
        ? await api.replaceCombinedCheckoutPaymentMethod({
          checkoutCapability,
          runtimeSessionRevision: checkoutSessionRevision,
          ...savePayload,
        })
        : await api.saveCheckoutPaymentMethod(checkoutToken, savePayload);
      const savedMethod = response?.payment_method;
      if (!savedMethod?.id) {
        throw new Error('Payment method was saved but the saved card details were unavailable.');
      }

      setLocalSavedMethods((current) => {
        const next = current
          .filter((item) => item.id !== editingSavedMethod.id && item.id !== savedMethod.id)
          .map((item) => ({ ...item, is_default: false }));
        return [{ ...savedMethod, is_default: true }, ...next];
      });
      setSelectedSavedMethod({ ...savedMethod, is_default: true });
      setPaymentChoice('saved');
      setEditingSavedMethod(null);
    } catch (err) {
      setError(err.message || 'Failed to save payment method.');
    } finally {
      setSavingPaymentMethod(false);
    }
  };

  // Coupon state
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponValid, setCouponValid] = useState(null);
  const [couponError, setCouponError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isInitialCoupon, setIsInitialCoupon] = useState(false);

  // State for live product data resolution
  const [resolvedProduct, setResolvedProduct] = useState(null);
  const [productResolving, setProductResolving] = useState(false);
  // Checkout mode: optional client-side resolve when backend doesn't return enriched primary_product
  const [checkoutResolvedProduct, setCheckoutResolvedProduct] = useState(null);
  const [checkoutProductResolving, setCheckoutProductResolving] = useState(false);
  const [imageLoadFailures, setImageLoadFailures] = useState({});

  // Read promo code from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const promo = params.get('promo');
    const promoSource = params.get('promo-source');
    const referralCampaign = params.get('referral-campaign');

    if (promo && promo.trim()) {
      setCouponCode(promo.trim());
      setCouponOpen(true);
      setIsInitialCoupon(true);
      sessionStorage.setItem('welliemd_coupon_code', promo.trim());
    } else if (referralCampaign && referralCampaign.trim()) {
      // Use affiliate slug as coupon code if no explicit promo is set
      setCouponCode(referralCampaign.trim());
      setCouponOpen(true);
      setIsInitialCoupon(true);
      sessionStorage.setItem('welliemd_coupon_code', referralCampaign.trim());
    }

    // Store referral info for order tracking
    if (referralCampaign) {
      sessionStorage.setItem('welliemd_referral_campaign', referralCampaign);
      sessionStorage.setItem('welliemd_referral_source', promoSource || 'affiliate');
    }
  }, []);

  // Coupon validation handler
  const handleValidateCoupon = async () => {
    if (paymentAdapter) return;
    const code = couponCode?.trim();
    if (!code) {
      setCouponError('Please enter a coupon code');
      setCouponValid(false);
      return;
    }

    setCouponValidating(true);
    setCouponError(null);
    setCouponValid(null);

    try {
      const result = await api.validateCoupon(
        code,
        finalPrice,
        selectedProduct?.product_id
      );

      if (result.valid) {
        setCouponValid(true);
        const appliedCode = code.toUpperCase();
        setAppliedCoupon({
          code: appliedCode,
          type: result.discount_type,
          value: result.value,
          discount: parseFloat(result.discount) || 0,
        });
        setCouponDiscount(parseFloat(result.discount) || 0);
        setCouponError(null);
        sessionStorage.setItem('welliemd_coupon_code', appliedCode);
      } else {
        setCouponValid(false);
        setCouponError(result.message || 'Invalid coupon code');
        setAppliedCoupon(null);
        setCouponDiscount(0);
        sessionStorage.removeItem('welliemd_coupon_code');
      }
    } catch (err) {
      setCouponValid(false);
      setCouponError(err.response?.data?.message || 'Failed to validate coupon');
      setAppliedCoupon(null);
      setCouponDiscount(0);
      sessionStorage.removeItem('welliemd_coupon_code');
    } finally {
      setCouponValidating(false);
    }
  };

  // Auto-validate coupon from URL when code is set
  useEffect(() => {
    if (isInitialCoupon && couponCode && couponValid === null && !couponValidating && !loading) {
      handleValidateCoupon();
    }
  }, [couponCode, loading, isInitialCoupon]);

  const handleCouponChange = (e) => {
    const value = e.target.value;
    setCouponCode(value);
    setIsInitialCoupon(false);
    // Clear validation state when input changes
    if (couponValid !== null) {
      setCouponValid(null);
      setCouponError(null);
      setAppliedCoupon(null);
      setCouponDiscount(0);
    }
    if (!value?.trim()) {
      sessionStorage.removeItem('welliemd_coupon_code');
    }
  };

  const handleCouponKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidateCoupon();
    }
  };

  // Extract selected product from responses
  const getSelectedProduct = () => {
    if (!responses || !questionnaire?.questions) {
      return null;
    }

    // Find product_selection question in questionnaire
    const productQuestion = questionnaire.questions.find(
      q => q.question_type === 'product_selection' && responses[q.id]
    );

    if (!productQuestion) {
      return null;
    }

    const responseValue = responses[productQuestion.id];

    if (responseValue && typeof responseValue === 'object' && responseValue.product_id) {
      return responseValue;
    }

    const selectedProduct = productQuestion.answer_choices?.find(
      choice => choice.product_id === responseValue
    );

    return selectedProduct;
  };

  const selectedProductFromResponse = getSelectedProduct();

  // Resolve live product data when a product is selected
  // This fetches current pricing/details from backend instead of using stale cached data
  useEffect(() => {
    if (paymentAdapter) return;
    const resolveProductData = async () => {
      const productId = selectedProductFromResponse?.product_id;

      if (!productId) {
        setResolvedProduct(null);
        return;
      }

      // Skip if already resolved for this product
      if (resolvedProduct?.product_id === productId) {
        return;
      }

      setProductResolving(true);
      try {
        const products = await api.resolveProducts([productId]);
        if (products && products.length > 0) {
          setResolvedProduct(products[0]);
          console.log('Resolved live product data:', products[0].product_name);
        } else {
          // Fallback to cached data if resolution fails
          console.warn('Product resolution returned empty, using cached data');
          setResolvedProduct(null);
        }
      } catch (err) {
        console.error('Failed to resolve product data:', err);
        // Fallback to cached data on error
        setResolvedProduct(null);
      } finally {
        setProductResolving(false);
      }
    };

    resolveProductData();
  }, [paymentAdapter, selectedProductFromResponse?.product_id]);

  // Use resolved product data if available, otherwise fall back to cached data
  // This ensures we always display the latest product information
  const selectedProduct = resolvedProduct || selectedProductFromResponse;

  const checkoutSummaryItems = Array.isArray(checkoutOrderSummary?.items)
    ? checkoutOrderSummary.items.filter(Boolean)
    : [];
  const checkoutSummaryItemCount = checkoutSummaryItems.length;
  const checkoutSummaryDisplayItems = checkoutSummaryItemCount > 0
    ? checkoutSummaryItems
    : (checkoutOrderSummary?.primary_product ? [checkoutOrderSummary.primary_product] : []);
  const checkoutProductId =
    checkoutSummaryDisplayItems?.[0]?.product_id ??
    checkoutSummaryDisplayItems?.[0]?.id ??
    null;
  const summaryHasEnrichedProduct =
    checkoutSummaryDisplayItems.some((item) =>
      item &&
      (item.original_price != null ||
       item.final_price != null ||
       item.total != null ||
       // Treatment-flow items from normalizeProduct() (previewSimulation.js)
       // carry the same pricing under these names instead -- the server
       // quote already enriched them, this is not a stale/legacy snapshot.
       item.price != null ||
       item.item_subtotal != null ||
       item.line_total != null)
    );
  useEffect(() => {
    if (paymentAdapter) return;
    if (!isCheckoutMode || !checkoutProductId || summaryHasEnrichedProduct) {
      if (!summaryHasEnrichedProduct && isCheckoutMode) setCheckoutResolvedProduct(null);
      return;
    }
    let cancelled = false;
    setCheckoutProductResolving(true);
    api.resolveProducts([checkoutProductId])
      .then((products) => {
        if (!cancelled && products && products.length > 0) setCheckoutResolvedProduct(products[0]);
        else if (!cancelled) setCheckoutResolvedProduct(null);
      })
      .catch(() => { if (!cancelled) setCheckoutResolvedProduct(null); })
      .finally(() => { if (!cancelled) setCheckoutProductResolving(false); });
    return () => { cancelled = true; };
  }, [paymentAdapter, isCheckoutMode, checkoutProductId, summaryHasEnrichedProduct]);

  // Calculate billing frequency from days_supply
  const getBillingFrequency = (daysSupply) => {
    if (!daysSupply) return 'monthly';
    if (daysSupply >= 90) return 'quarterly';
    if (daysSupply >= 60) return 'bi-monthly';
    return 'monthly';
  };

  const billingFrequency = selectedProduct?.days_supply
    ? getBillingFrequency(selectedProduct.days_supply)
    : 'monthly';

  const productName = selectedProduct?.product_name || '';
  const productDescription = selectedProduct?.description || '';
  const originalPrice = parseFloat(selectedProduct?.original_price) || 0;
  const discountedPrice = selectedProduct?.discounted_price ? parseFloat(selectedProduct.discounted_price) : null;
  const finalPrice = parseFloat(selectedProduct?.final_price) || 0;
  const shippingFee = parseFloat(selectedProduct?.shipping_fee) || 0;
  const total = parseFloat(selectedProduct?.total) || 0;  // Backend-calculated, this is the chargeable amount
  const hasDiscount = selectedProduct?.has_discount || (discountedPrice !== null && discountedPrice < originalPrice);

  // Checkout mode: prefer summary primary_product (locked snapshot at order creation); resolve only for legacy orders
  const pp = checkoutOrderSummary?.primary_product;
  const checkoutProduct =
    (pp && (pp.original_price != null || pp.final_price != null || pp.total != null) ? pp : null) ?? checkoutResolvedProduct;
  const checkoutLineItems = checkoutSummaryDisplayItems.map((item) => {
    const itemPrice = parseFloat(item?.price ?? item?.final_price ?? item?.total ?? item?.amount ?? 0) || 0;
    const itemQuantity = parseFloat(item?.quantity ?? 1) || 1;
    const itemShipping = parseFloat(item?.shipping_fee ?? 0) || 0;
    return {
      key: item?.id ?? item?.product_id ?? item?.name,
      name: item?.product_name || item?.name || 'Product',
      description: item?.description || '',
      quantity: itemQuantity,
      unitPrice: itemPrice,
      shippingFee: itemShipping,
      isSupply: Boolean(item?.is_supply),
      isIncluded: Boolean(item?.is_included),
      image: item?.product_image || '',
    };
  });
  const checkoutItemsTotal = checkoutLineItems.reduce((sum, item) => {
    if (item.isSupply && item.isIncluded) return sum;
    return sum + (item.unitPrice * item.quantity);
  }, 0);
  const checkoutItemsShipping = checkoutLineItems.reduce((sum, item) => sum + (item.shippingFee * item.quantity), 0);
  const checkoutItemsGrandTotal = checkoutItemsTotal + checkoutItemsShipping;
  const checkoutMedicationItems = checkoutLineItems.filter((item) => !item.isSupply);
  const checkoutSupplyItems = checkoutLineItems.filter((item) => item.isSupply);
  const displayProductName = isCheckoutMode && checkoutOrderSummary
    ? (checkoutLineItems.length > 1 ? `${checkoutLineItems.length} selected products` : (checkoutProduct?.product_name || checkoutProduct?.name || pp?.name || 'Order'))
    : productName;
  const displayProductDescription = isCheckoutMode && checkoutOrderSummary
    ? (checkoutLineItems.length > 1 ? 'Multiple selected products' : (checkoutProduct?.description ?? (pp?.description ?? (checkoutOrderSummary.display_id ? `Order #${checkoutOrderSummary.display_id}` : ''))))
    : productDescription;
  const checkoutOriginalPrice = checkoutProduct ? parseFloat(checkoutProduct.original_price) || 0 : 0;
  const checkoutBasePrice = checkoutProduct
    ? parseFloat(checkoutProduct.base_price || checkoutProduct.final_price || checkoutProduct.price) || 0
    : 0;
  const checkoutShippingFee = checkoutProduct ? parseFloat(checkoutProduct.shipping_fee) || 0 : 0;
  const checkoutTotalFromProduct = checkoutProduct ? parseFloat(checkoutProduct.total) || 0 : 0;
  const checkoutHasDiscount = checkoutProduct?.has_discount
    ?? (checkoutOriginalPrice > 0 && checkoutBasePrice > 0 && checkoutBasePrice < checkoutOriginalPrice);
  const displayTotal = isCheckoutMode && checkoutOrderSummary
    ? (parseFloat(checkoutOrderSummary.amount) || checkoutTotalFromProduct || (checkoutItemsTotal > 0 ? checkoutItemsTotal + checkoutItemsShipping : 0))
    : total;
  const checkoutSubtotal = checkoutLineItems.length > 1
    ? checkoutItemsTotal
    : (
      checkoutBasePrice
      || (checkoutTotalFromProduct > 0 ? Math.max(0, checkoutTotalFromProduct - checkoutShippingFee) : 0)
      || (displayTotal > 0 ? Math.max(0, displayTotal - checkoutShippingFee) : 0)
    );
  const displayFinalPrice = isCheckoutMode
    ? checkoutSubtotal
    : finalPrice;
  // Checkout mode: show "Billed monthly" (or frequency from days_supply) when product data exists
  const checkoutBillingFrequency = checkoutProduct?.days_supply
    ? getBillingFrequency(checkoutProduct.days_supply)
    : 'monthly';
  const displayBillingLabel = isCheckoutMode
    ? (checkoutProduct ? `Billed ${checkoutBillingFrequency}` : 'Checkout')
    : `Billed ${billingFrequency}`;
  const getProductImageUrl = (rawImage) => {
    if (!rawImage || typeof rawImage !== 'string') return '';
    if (/^https?:\/\//i.test(rawImage) || rawImage.startsWith('data:')) {
      return rawImage;
    }

    const clientApiEndpoint = window.localStorage.getItem('welliemd_client_api_endpoint') || '';
    if (!clientApiEndpoint) return rawImage;

    try {
      const apiUrl = new URL(clientApiEndpoint);
      return new URL(rawImage, apiUrl.origin).toString();
    } catch (_error) {
      return rawImage;
    }
  };
  const rawSummaryImage = isCheckoutMode
    ? (checkoutLineItems[0]?.image || checkoutProduct?.product_image || pp?.product_image)
    : selectedProduct?.product_image;
  const productImageUrl = getProductImageUrl(rawSummaryImage);
  const summaryImageKey = `${isCheckoutMode ? 'checkout' : 'questionnaire'}:${checkoutProductId || selectedProduct?.product_id || displayProductName}`;
  const showProductImage = Boolean(productImageUrl) && !imageLoadFailures[summaryImageKey];

  // Determine whether the gateway config contains the minimum required keys
  function hasGatewayKeys(gateway, cfg) {
    if (!gateway || !cfg) return false;
    switch (gateway) {
      case 'stripe':
        return !!(
          cfg.stripe_publishable_key ||
          cfg.publishable_key ||
          cfg.stripe?.publishable_key
        );
      case 'nmi':
        return !!(cfg.nmi_script_url && (cfg.nmi_client_key || process.env.REACT_APP_NMI_CLIENT_KEY));
      case 'authorize_net':
        return !!(
          (cfg.authorize_net_client_key || cfg.clientKey || process.env.REACT_APP_AUTHORIZE_NET_CLIENT_KEY) &&
          (cfg.authorize_net_api_login || cfg.apiLoginID || process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID)
        );
      default:
        return false;
    }
  }

  const gatewayReady = hasGatewayKeys(activeGateway, gatewayConfig);

  // Checkout mode: use payment config from props
  useEffect(() => {
    if (!isCheckoutMode || !checkoutPaymentConfig) return;
    const gateway =
      checkoutPaymentConfig.active_gateway ||
      checkoutPaymentConfig.activeGateway ||
      null;
    if (gateway) {
      const cfg = getGatewayConfig(gateway, checkoutPaymentConfig);
      setActiveGateway(gateway);
      setGatewayConfig(cfg);
    } else {
      setActiveGateway(null);
      setGatewayConfig(null);
    }
    setLoading(false);
  }, [isCheckoutMode, checkoutPaymentConfig]);

  useEffect(() => {
    if (paymentAdapter) {
      setActiveGateway(paymentAdapter.gateway || "preview");
      setGatewayConfig({});
      setLoading(false);
      return;
    }
    if (isCheckoutMode && checkoutPaymentConfig) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    // Prefer backend-provided payment-config; fall back to env variables only if backend doesn't provide a gateway
    (async () => {
      try {
        const res = await api.getPaymentConfig();
        if (!mounted) return;

        // API may return either { success: true, data: {...} } or the config object directly
        const data = res?.data || res || null;
        const gateway = data?.active_gateway || data?.activeGateway || null;

        if (gateway) {
          const cfg = getGatewayConfig(gateway, data);
          setActiveGateway(gateway);
          setGatewayConfig(cfg);
          if (mounted) setLoading(false);
          return;
        }
      } catch (err) {
        // swallow - we'll attempt env fallback below
        console.debug('Payment config backend call failed (will try env):', err && err.message ? err.message : err);
      }

      // Backend didn't provide a gateway; try env variables as a fallback
      const envGateway = deriveGatewayFromEnv();
      if (envGateway) {
        const envConfig = getGatewayConfig(envGateway, null);
        setActiveGateway(envGateway);
        setGatewayConfig(envConfig);
      } else {
        setActiveGateway(null);
        setGatewayConfig(null);
      }

      if (mounted) setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [paymentAdapter, isCheckoutMode, checkoutPaymentConfig]);

  function deriveGatewayFromEnv() {
    // Check canonical env vars for gateways (prefer canonical names; legacy fallbacks kept)
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || process.env.REACT_APP_STRIPE_PUB_KEY || null;
    const nmiScript = process.env.REACT_APP_NMI_SCRIPT_URL || null;
    const anetKey = process.env.REACT_APP_AUTHORIZE_NET_CLIENT_KEY || process.env.REACT_APP_ANET_CLIENT_KEY || null;

    if (stripeKey) return 'stripe';
    if (nmiScript) return 'nmi';
    if (anetKey) return 'authorize_net';
    // Optional: allow forcing the gateway via env
    const forced = process.env.REACT_APP_PAYMENT_GATEWAY || null;
    if (forced) return forced;
    return null;
  }

  function getGatewayConfig(gateway, backendData = null) {
    if (!gateway) return null;

    const config = { ...backendData };

    switch (gateway) {
      case 'stripe':
        config.stripe_publishable_key =
          backendData?.stripe_publishable_key ||
          backendData?.publishable_key ||
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
          process.env.REACT_APP_STRIPE_PUB_KEY ||
          null;
        break;

      case 'nmi':
        config.nmi_script_url =
          backendData?.nmi_script_url ||
          process.env.REACT_APP_NMI_SCRIPT_URL ||
          'https://secure.networkmerchants.com/token/Collect.js';
        config.nmi_client_key =
          backendData?.nmi_client_key ||
          process.env.REACT_APP_NMI_CLIENT_KEY ||
          null;
        break;

      case 'authorize_net':
        config.authorize_net_client_key =
          backendData?.authorize_net_client_key ||
          backendData?.clientKey ||
          process.env.REACT_APP_AUTHORIZE_NET_CLIENT_KEY ||
          process.env.REACT_APP_ANET_CLIENT_KEY ||
          null;
        config.authorize_net_api_login =
          backendData?.authorize_net_api_login ||
          backendData?.apiLoginID ||
          process.env.REACT_APP_AUTHORIZE_NET_API_LOGIN_ID ||
          process.env.REACT_APP_ANET_API_LOGIN_ID ||
          null;
        config.authorize_net_environment =
          backendData?.authorize_net_environment ||
          process.env.REACT_APP_AUTHORIZE_NET_ENV ||
          'sandbox';
        break;

      default:
        return config;
    }

    return config;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Delegate to gateway-specific form for tokenization
    if (childRef.current && typeof childRef.current.getPaymentData === 'function') {
      try {
        const paymentData = await childRef.current.getPaymentData();
        // Payment data will be handled by the parent component
      } catch (err) {
        setError(err.message || 'Payment processing failed');
      }
    }
  };

  useImperativeHandle(ref, () => ({
    async getPaymentData() {
      if (paymentAdapter) return paymentAdapter.getPaymentData();
      if (!activeGateway) {
        return { gateway: null, payment_data: null };
      }

      // Checkout: use saved card if selected
      if (isCheckoutMode && hasSavedMethods && paymentChoice === 'saved' && selectedSavedMethod) {
        const referralCampaign = sessionStorage.getItem('welliemd_referral_campaign');
        const referralSource = sessionStorage.getItem('welliemd_referral_source');
        let payment_data;
        if (activeGateway === 'stripe' && selectedSavedMethod.stripe_payment_method_id) {
          payment_data = { payment_method_id: selectedSavedMethod.stripe_payment_method_id };
          if (selectedSavedMethod.billing_postal_code) payment_data.postal_code = selectedSavedMethod.billing_postal_code;
        } else if (activeGateway === 'nmi' || activeGateway === 'authorize_net') {
          payment_data = { saved_payment_method_id: selectedSavedMethod.id };
        } else {
          payment_data = null;
        }
        if (!payment_data) {
          return { gateway: activeGateway, payment_data: null };
        }
        return {
          gateway: activeGateway,
          payment_data,
          coupon_code: checkoutAppliedCoupon?.code || null,
          coupon_discount: checkoutCouponDiscount,
          referral_campaign: referralCampaign || null,
          referral_source: referralSource || null,
          final_total: Math.max(0, displayTotal - checkoutCouponDiscount),
        };
      }

      // New card: delegate to child gateway form
      if (isCheckoutMode && editingSavedMethod) {
        throw new Error('Save the replacement card before paying.');
      }
      if (childRef.current && typeof childRef.current.getPaymentData === 'function') {
        const paymentData = await childRef.current.getPaymentData();

        const referralCampaign = sessionStorage.getItem('welliemd_referral_campaign');
        const referralSource = sessionStorage.getItem('welliemd_referral_source');

        return {
          gateway: activeGateway,
          payment_data: paymentData,
          coupon_code: isCheckoutMode ? (checkoutAppliedCoupon?.code || null) : (appliedCoupon?.code || null),
          coupon_discount: isCheckoutMode ? checkoutCouponDiscount : couponDiscount,
          referral_campaign: referralCampaign || null,
          referral_source: referralSource || null,
          final_total: isCheckoutMode ? Math.max(0, displayTotal - checkoutCouponDiscount) : Math.max(0, total - couponDiscount),
        };
      }

      return { gateway: activeGateway, payment_data: null };
    },
  }));

  if (loading) {
    return <LoadingSpinner text="Loading payment options..." />;
  }

  if (error && !activeGateway) {
    return (
      <ErrorCard
        message="Payment configuration not found. Please check your environment variables or contact support."
        variant="error"
      />
    );
  }

  return (
    <PaymentSection>
      {/* Order Summary Section */}
      {(!isCheckoutMode || !hideCheckoutOrderSummary) && (
      <OrderSummary>
        <OrderSummaryTitle>{isCheckoutMode ? 'Order summary' : 'Total if prescribed'}</OrderSummaryTitle>

        {isCheckoutMode && checkoutMedicationItems.length > 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {checkoutMedicationItems.map((item) => (
                <div key={item.key} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <PillImageWrap aria-hidden="true">
                    {item.image ? (
                      <ProductImage src={item.image} alt={item.name} />
                    ) : (
                      <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" role="img">
                        <rect width="36" height="36" rx="8" fill="#F0F6F9" />
                        <g transform="translate(8,8)">
                          <ellipse cx="10" cy="6" rx="8" ry="6" fill="#E6F2F6" />
                          <rect x="2" y="10" width="16" height="6" rx="3" fill="#DDEFF3" />
                        </g>
                      </svg>
                    )}
                  </PillImageWrap>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <ProductText>
                      <ProductTitle>{item.name}</ProductTitle>
                      <ProductSubtitle>
                        {item.quantity > 1 ? `${item.quantity} x ` : ''}
                        Medication
                      </ProductSubtitle>
                    </ProductText>
                    <div style={{ fontSize: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      ${Number((item.unitPrice * item.quantity) + (item.shippingFee * item.quantity)).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {checkoutSupplyItems.length > 0 && (
              <div style={{ borderTop: '1px solid var(--zyvgo-border-light, #e0e0e0)', paddingTop: '12px' }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--zyvgo-text-secondary, #6b7280)', marginBottom: '10px' }}>
                  Supplies
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {checkoutSupplyItems.map((item) => (
                    <div key={item.key} style={{ display: 'flex', gap: '12px', alignItems: 'center', paddingLeft: '8px' }}>
                      <PillImageWrap aria-hidden="true">
                        <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" role="img">
                          <rect width="36" height="36" rx="8" fill="#F5F7FA" />
                          <g transform="translate(8,8)">
                            <ellipse cx="10" cy="6" rx="8" ry="6" fill="#E9EEF4" />
                            <rect x="2" y="10" width="16" height="6" rx="3" fill="#D9E2EC" />
                          </g>
                        </svg>
                      </PillImageWrap>
                      <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <ProductText>
                          <ProductTitle>{item.name}</ProductTitle>
                          <ProductSubtitle>
                            {item.quantity > 1 ? `${item.quantity} x ` : ''}
                            {item.isIncluded ? 'Included supply' : 'Supply'}
                          </ProductSubtitle>
                        </ProductText>
                        <div style={{ fontSize: '1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                          {item.isIncluded ? '$0.00' : `+$${Number((item.unitPrice * item.quantity) + (item.shippingFee * item.quantity)).toFixed(2)}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <ProductRow>
            <PillImageWrap aria-hidden={showProductImage ? undefined : "true"}>
              {showProductImage ? (
                <ProductImage
                  src={productImageUrl}
                  alt={displayProductName}
                  onError={() =>
                    setImageLoadFailures((prev) => ({ ...prev, [summaryImageKey]: true }))
                  }
                />
              ) : (
                <svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg" role="img">
                  <rect width="36" height="36" rx="8" fill="#F0F6F9" />
                  <g transform="translate(8,8)">
                    <ellipse cx="10" cy="6" rx="8" ry="6" fill="#E6F2F6" />
                    <rect x="2" y="10" width="16" height="6" rx="3" fill="#DDEFF3" />
                  </g>
                </svg>
              )}
            </PillImageWrap>

            <ProductText>
              <ProductTitle>{displayProductName}</ProductTitle>
              <ProductSubtitle>{displayProductDescription}</ProductSubtitle>
              <BillingLabel>{displayBillingLabel}</BillingLabel>
            </ProductText>
          </ProductRow>
        )}

        <PriceGroup>
          {/* Product Price with discount display */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
            {(isCheckoutMode ? checkoutHasDiscount : hasDiscount) && (
              <OriginalPrice>${Number(isCheckoutMode ? checkoutOriginalPrice : originalPrice).toFixed(2)}</OriginalPrice>
            )}
            <DiscountedPrice>${Number(displayFinalPrice).toFixed(2)}</DiscountedPrice>
          </div>
          {(isCheckoutMode ? (checkoutLineItems.length > 1 ? checkoutItemsShipping > 0 : checkoutShippingFee > 0) : shippingFee > 0) && (
            <div style={{ fontSize: 'var(--zyvgo-small-size, 0.875rem)', color: 'var(--zyvgo-text-secondary, #666666)', marginTop: '4px' }}>
              + ${Number(isCheckoutMode ? (checkoutLineItems.length > 1 ? checkoutItemsShipping : checkoutShippingFee) : shippingFee).toFixed(2)} shipping
            </div>
          )}
        </PriceGroup>

        {/* Pricing Breakdown */}
        <div style={{
          borderTop: '1px solid var(--zyvgo-border-light, #e0e0e0)',
          marginTop: '16px',
          paddingTop: '16px',
          fontSize: 'var(--zyvgo-small-size, 0.875rem)'
        }}>
          {(isCheckoutMode && (checkoutSubtotal > 0 || (checkoutLineItems.length > 1 ? checkoutItemsShipping > 0 : checkoutShippingFee > 0))) ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'var(--zyvgo-text-secondary, #666666)' }}>
                <span>Subtotal</span>
                <span>${Number(checkoutSubtotal).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'var(--zyvgo-text-secondary, #666666)' }}>
                <span>Shipping</span>
                <span>${Number(checkoutLineItems.length > 1 ? checkoutItemsShipping : checkoutShippingFee).toFixed(2)}</span>
              </div>
            </>
          ) : !isCheckoutMode && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'var(--zyvgo-text-secondary, #666666)' }}>
                <span>Subtotal</span>
                <span>${Number(finalPrice).toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'var(--zyvgo-text-secondary, #666666)' }}>
                <span>Shipping</span>
                <span>${Number(shippingFee).toFixed(2)}</span>
              </div>
            </>
          )}

          {/* Coupon Discount Row (questionnaire and checkout: show when coupon applied) */}
          {((!isCheckoutMode && appliedCoupon && couponDiscount > 0) ||
            (isCheckoutMode && checkoutAppliedCoupon && checkoutCouponDiscount > 0)) && (
            <DiscountRow>
              <span>Coupon ({isCheckoutMode ? checkoutAppliedCoupon.code : appliedCoupon.code})</span>
              <span>-${Number(isCheckoutMode ? checkoutCouponDiscount : couponDiscount).toFixed(2)}</span>
            </DiscountRow>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontWeight: '600',
            fontSize: 'var(--zyvgo-body-size, 1rem)',
            color: 'var(--zyvgo-text-primary, #111111)',
            paddingTop: '8px',
            borderTop: '1px solid var(--zyvgo-border-light, #e0e0e0)'
          }}>
            <span>{isCheckoutMode ? 'Amount due' : 'Total'}</span>
            <span>${Math.max(0, displayTotal - (isCheckoutMode ? checkoutCouponDiscount : couponDiscount)).toFixed(2)}</span>
          </div>
        </div>

        {/* Coupon Input Section (questionnaire only) */}
        {!isCheckoutMode && !paymentAdapter && (
        <CouponSection>
          <CouponToggle onClick={() => setCouponOpen(!couponOpen)}>
            Have a coupon code? {couponOpen ? '−' : '+'}
          </CouponToggle>

          {couponOpen && (
            <>
              <CouponInputRow>
                <CouponInput
                  type="text"
                  value={couponCode}
                  onChange={handleCouponChange}
                  onKeyPress={handleCouponKeyPress}
                  placeholder="Enter coupon code"
                  disabled={couponValidating}
                  className={couponValid === true ? 'valid' : couponValid === false ? 'invalid' : ''}
                />
                <CouponApplyButton
                  type="button"
                  onClick={handleValidateCoupon}
                  disabled={couponValidating || !couponCode?.trim()}
                >
                  {couponValidating ? '...' : 'Apply'}
                </CouponApplyButton>
              </CouponInputRow>

              {couponValid === true && appliedCoupon && (
                <CouponSuccess>
                  <span>✓</span> Coupon applied: {appliedCoupon.type === 'fixed' ? `$${appliedCoupon.value} off` : `${appliedCoupon.value}% off`}
                </CouponSuccess>
              )}

              {couponError && (
                <CouponError>{couponError}</CouponError>
              )}
            </>
          )}
        </CouponSection>
        )}
      </OrderSummary>
      )}

      {/* Payment Method Section */}
      <PaymentMethodSection>
        {!hidePaymentHeading && (
          <>
            <PaymentHeader>
              <PaymentTitle>Payment Information</PaymentTitle>
              <SecureBadge>
                <SecureIcon
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                    clipRule="evenodd"
                  />
                </SecureIcon>
                <span>Secure</span>
              </SecureBadge>
            </PaymentHeader>

            <PaymentSubheading>Payment</PaymentSubheading>

            <PaymentDescription>
              All transactions are secure and encrypted.
            </PaymentDescription>
          </>
        )}

        {hasSavedMethods && (
          <SavedCardChoiceList>
            {savedMethods.map((m) => (
              <SavedCardOption
                key={m.id}
                role="button"
                tabIndex={0}
                className={paymentChoice === 'saved' && selectedSavedMethod?.id === m.id ? 'selected' : ''}
                onClick={() => {
                  setPaymentChoice('saved');
                  setSelectedSavedMethod(m);
                  setEditingSavedMethod(null);
                }}
                onKeyDown={(event) => handleCardOptionKeyDown(event, () => {
                  setPaymentChoice('saved');
                  setSelectedSavedMethod(m);
                  setEditingSavedMethod(null);
                })}
              >
                <SavedCardContent>
                  {m.card_brand ? `${m.card_brand.charAt(0).toUpperCase() + m.card_brand.slice(1)} ` : ''}
                  {m.masked_card_number || `••••`}
                  {(m.card_expiry_month && m.card_expiry_year) ? ` · Exp ${String(m.card_expiry_month).padStart(2, '0')}/${String(m.card_expiry_year).slice(-2)}` : ''}
                  {m.is_default ? ' (default)' : ''}
                </SavedCardContent>
                <SavedCardActions>
                  <SavedCardActionButton
                    type="button"
                    onClick={(event) => handleEditSavedMethod(event, m)}
                  >
                    Edit
                  </SavedCardActionButton>
                  <SavedCardActionButton
                    type="button"
                    disabled={removingPaymentMethodId === m.id}
                    onClick={(event) => handleRemoveSavedMethod(event, m)}
                  >
                    {removingPaymentMethodId === m.id ? 'Removing...' : 'Remove'}
                  </SavedCardActionButton>
                </SavedCardActions>
              </SavedCardOption>
            ))}
            <NewCardOption
              role="button"
              tabIndex={0}
              className={paymentChoice === 'new' ? 'selected' : ''}
              onClick={handleEnterNewCard}
              onKeyDown={(event) => handleCardOptionKeyDown(event, handleEnterNewCard)}
            >
              <span>Enter new card</span>
              <CardIcons>
                <IconWrap aria-hidden="true"><Visa width={36} height={22} /></IconWrap>
                <IconWrap aria-hidden="true"><Mastercard width={36} height={22} /></IconWrap>
                <IconWrap aria-hidden="true"><Amex width={36} height={22} /></IconWrap>
                <IconWrap aria-hidden="true"><Discover width={36} height={22} /></IconWrap>
              </CardIcons>
            </NewCardOption>
          </SavedCardChoiceList>
        )}

        {/* In questionnaire mode, show a compact \"selected method\" banner.
            For checkout, the design shows only the card list, so we skip this. */}
        {!isCheckoutMode && (!hasSavedMethods || paymentChoice === 'saved') && (
          <SelectedMethodDisplay>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <MethodLabel>
                {hasSavedMethods && selectedSavedMethod
                  ? `${selectedSavedMethod.card_brand ? selectedSavedMethod.card_brand.charAt(0).toUpperCase() + selectedSavedMethod.card_brand.slice(1) : 'Card'} ${selectedSavedMethod.masked_card_number || '••••'}`
                  : 'Credit card'}
              </MethodLabel>
              {(!hasSavedMethods || !selectedSavedMethod) && (
                <CardIcons>
                  <IconWrap aria-hidden="true"><Visa width={36} height={22} /></IconWrap>
                  <IconWrap aria-hidden="true"><Mastercard width={36} height={22} /></IconWrap>
                  <IconWrap aria-hidden="true"><Amex width={36} height={22} /></IconWrap>
                  <IconWrap aria-hidden="true"><Discover width={36} height={22} /></IconWrap>
                </CardIcons>
              )}
            </div>
          </SelectedMethodDisplay>
        )}

        {!isCheckoutMode && hasSavedMethods && paymentChoice === 'new' && (
          <SelectedMethodDisplay>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <MethodLabel>Credit card</MethodLabel>
              <CardIcons>
                <IconWrap aria-hidden="true"><Visa width={36} height={22} /></IconWrap>
                <IconWrap aria-hidden="true"><Mastercard width={36} height={22} /></IconWrap>
                <IconWrap aria-hidden="true"><Amex width={36} height={22} /></IconWrap>
                <IconWrap aria-hidden="true"><Discover width={36} height={22} /></IconWrap>
              </CardIcons>
            </div>
          </SelectedMethodDisplay>
        )}
      </PaymentMethodSection>

      {/* Payment Form Section - show only when entering new card or no saved methods */}
      <PaymentFormSection>
        {editingSavedMethod && (
          <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'var(--zyvgo-bg-subtle, #f9fafb)', border: '1px solid var(--zyvgo-border, #e1e5e9)', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--zyvgo-text-primary, #111111)' }}>Replacing Card</h4>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--zyvgo-text-secondary, #666666)' }}>
              {editingSavedMethod.card_brand ? `${editingSavedMethod.card_brand.charAt(0).toUpperCase() + editingSavedMethod.card_brand.slice(1)} ` : ''}
              {editingSavedMethod.masked_card_number || `••••`}
              {(editingSavedMethod.card_expiry_month && editingSavedMethod.card_expiry_year) ? ` · Exp ${String(editingSavedMethod.card_expiry_month).padStart(2, '0')}/${String(editingSavedMethod.card_expiry_year).slice(-2)}` : ''}
            </p>
          </div>
        )}
        {/* Child form (Stripe Elements mount or hosted inputs) */}
        {paymentAdapter && paymentAdapter.renderForm?.()}

        {!paymentAdapter && (!hasSavedMethods || paymentChoice === 'new') && activeGateway === 'stripe' && (
          gatewayReady ? (
            <StripeForm ref={childRef} gatewayConfig={gatewayConfig} />
          ) : (
            <div style={{ color: 'var(--zyvgo-text-secondary, #666666)', fontSize: '0.875rem', marginBottom: '12px' }}>
              Waiting for Stripe configuration...
            </div>
          )
        )}

        {!paymentAdapter && (!hasSavedMethods || paymentChoice === 'new') && activeGateway === 'nmi' && (
          gatewayReady ? (
            <NmiCollectForm ref={childRef} gatewayConfig={gatewayConfig} />
          ) : (
            <div style={{ color: 'var(--zyvgo-text-secondary, #666666)', fontSize: '0.875rem', marginBottom: '12px' }}>
              Waiting for NMI configuration...
            </div>
          )
        )}

        {!paymentAdapter && (!hasSavedMethods || paymentChoice === 'new') && activeGateway === 'authorize_net' && (
          gatewayReady ? (
            <AuthorizeNetAcceptForm ref={childRef} gatewayConfig={gatewayConfig} />
          ) : (
            <div style={{ color: 'var(--zyvgo-text-secondary, #666666)', fontSize: '0.875rem', marginBottom: '12px' }}>
              Waiting for Authorize.Net configuration...
            </div>
          )
        )}

        {isCheckoutMode && editingSavedMethod && (
          <SaveCardButton
            type="button"
            onClick={handleSaveEditedCard}
            disabled={savingPaymentMethod}
          >
            {savingPaymentMethod ? 'Saving...' : 'Save Card'}
          </SaveCardButton>
        )}

        {error && (
          <ErrorText style={{ color: 'var(--zyvgo-error, #c00)', fontSize: 'var(--zyvgo-small-size, 0.875rem)', marginBottom: 'var(--zyvgo-spacing-sm, 12px)' }}>
            {error}
          </ErrorText>
        )}

        <PaymentFormHelper>
          Your payment info will be securely stored. You won't be charged yet.
        </PaymentFormHelper>
      </PaymentFormSection>
    </PaymentSection>
  );
});

export default PaymentStep;
